import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger
} from '@dextech-ai/dex-design-system';
import Button from '@/componentsDS/Button/Button';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';

const groupByCategory = (options) => options.reduce((groups, option) => {
	const category = option.category || 'uncategorized';
	(groups[category] = groups[category] || []).push(option);
	return groups;
}, {});

const FilterMenu = ({ options = [], value = [], onChange, trigger, id, isLoading = false, noResults = false }) => {
	const [open, setOpen] = useState(false);
	const selectedIds = new Set(value.map((option) => option.id));
	const allSelected = options.length > 0 && options.every((option) => selectedIds.has(option.id));

	const toggle = (option) => onChange(
		selectedIds.has(option.id) ? value.filter((selected) => selected.id !== option.id) : [...value, option]
	);

	const renderOptions = () => {
		if (isLoading) {
			return <DropdownMenuItem itemKey="loading" disabled>Loading…</DropdownMenuItem>;
		}
		if (noResults) {
			return <DropdownMenuItem itemKey="no-results" disabled>No results found.</DropdownMenuItem>;
		}
		return (
			<>
				<DropdownMenuCheckboxItem itemKey="all-options" checked={allSelected} onCheckedChange={() => onChange(allSelected ? [] : options)}>
					<span id={id}>All options</span>
				</DropdownMenuCheckboxItem>
				{Object.entries(groupByCategory(options)).map(([category, categoryOptions]) => (
					<Fragment key={category}>
						{category !== 'uncategorized' && <DropdownMenuLabel itemKey={`label-${category}`}>{category}</DropdownMenuLabel>}
						{categoryOptions.map((option) => (
							<DropdownMenuCheckboxItem
								key={`${category}-${option.id}`}
								itemKey={`${category}-${option.id}`}
								checked={selectedIds.has(option.id)}
								disabled={option.isDisabled}
								onCheckedChange={() => toggle(option)}
							>
								<span id={id}>{option.label}</span>
							</DropdownMenuCheckboxItem>
						))}
					</Fragment>
				))}
			</>
		);
	};

	return (
		<DropdownMenu
			placement="bottomLeft"
			overlayClassName="onboarding-filter-menu"
			open={open}
			onOpenChange={(next, info) => {
				if (next || info?.source !== 'menu') setOpen(next);
			}}
		>
			<DropdownMenuTrigger asChild>
				<div className="DS__dropdown-wrapper">
					<Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} dataTestId={id} text={trigger} />
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent>{renderOptions()}</DropdownMenuContent>
		</DropdownMenu>
	);
};

FilterMenu.propTypes = {
	options: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		label: PropTypes.string,
		category: PropTypes.string,
		isDisabled: PropTypes.bool
	})),
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	trigger: PropTypes.node.isRequired,
	id: PropTypes.string,
	isLoading: PropTypes.bool,
	noResults: PropTypes.bool
};

export default FilterMenu;
